import { mapActions } from 'vuex'
import FormErrors from 'app/utils/form_errors'

/**
 * Defines shared data and functions necessary for account fetching, creation, modification, and deletion.
 */
export const StartMixin = {
  /**
   * The structure and defaults of the common data required for account creation.
   *
   * @returns {Object} - An object for the sign up email, any errors generated during sign up, and a boolean indicating
   *                     that system is processing/awaiting the return from the backend account creation process.
   */
  data() {
    return {
      email: '',
      acceptPolicies: false,
      errors: new FormErrors(),
      isSending: false
    }
  },

  computed: {
    isOkToCreate() {
      return this.acceptPolicies
    },

    hasAccount() {
      return !!this.$account
    }
  },

  methods: {
    /**
     * Makes {@link ../../store/modules/accounts.js#createFreeAccount} available.
     *
     * Details:
     * Modules defined here must be added to {@link ../../store/index.js} to be added to mapActions.
     *
     * Convenience Links:
     * {@link https://v3.vuex.vuejs.org/guide/actions.html#dispatching-actions-in-components Vuex Map Actions}
     */
    ...mapActions('accounts', ['createFreeAccount']),

    /**
     * Checks that a valid email was provided by the user. If so it starts the account creation process. Otherwise, it
     * returns nothing.
     *
     * Details:
     * Function requires caller to provide an email ref which is an HTML input of type email, see Vue Template Refs below
     * for details.
     * Function uses client-side form validation to determine if the email is valid, see HTML Form Email Validation and
     * Client-Side Form Validation below for details.
     * If email is valid, set isSending to true. This may be used by the caller to indicate that the function is awaiting a
     * return.
     * Function calls {@link ../../store/modules/accounts.js#createFreeAccount}.
     * Function calls {@link ../../plugins/shortcuts.js#install}.
     * Function routes to confirmation page upon success.
     *
     * Convenience Links:
     * {@link https://vuejs.org/guide/essentials/template-refs.html Vue Template Refs}
     * {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#validation HTML Form Email Validation}
     * {@link https://developer.mozilla.org/en-US/docs/Learn/Forms/Form_validation#validating_forms_using_javascript Client-Side Form Validation}
     * {@link https://v3.router.vuejs.org/guide/essentials/navigation.html#router-push-location-oncomplete-onabort Vue JS Router: push}
     *
     * @async
     * @function initiateFreeAccount
     */
    async initiateFreeAccount() {
      if (this.$refs.email.validity.valid && this.acceptPolicies) {
        try {
          this.isSending = true

          this.email = this.email.trim().toLowerCase()
          this.$posthog.capture('account_create_start', { location: 'start_view' })
          await this.createFreeAccount(this.email)

          this.errors = new FormErrors()

          this.$router.push({ name: 'start.confirm' })
        } catch (errors) {
          this.errors = errors
        } finally {
          this.isSending = false
        }
      }
    }
  }
}
